import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react";
import leaveService from "./leaveService";

const LeaveApproval = ({ defaultTab = "approvals" }) => {
  const toast = useToast();
  const [tab, setTab] = useState(defaultTab);
  const cardBg = useColorModeValue("white", "gray.800");
  const subtleBg = useColorModeValue("gray.50", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Get user and check authorization
  const getUserFromStorage = () => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  };

  const user = getUserFromStorage();
  const APPROVAL_ROLES = [
    "SuperAdmin",
    "Admin",
    "HRHead",
    "HRExecutive",
    "Manager",
  ];
  const canApprove = APPROVAL_ROLES.includes(user?.role);

  const [pendingRequests, setPendingRequests] = useState([]);
  const [rejectReasonById, setRejectReasonById] = useState({});

  const [policies, setPolicies] = useState([]);
  const [policyForm, setPolicyForm] = useState({
    name: "",
    code: "",
    type: "Paid",
    yearlyQuota: 12,
    carryForwardAllowed: true,
    maxCarryForward: 5,
    requiresApproval: true,
    allowHalfDay: true,
  });

  const [calendars, setCalendars] = useState([]);
  const [calendarForm, setCalendarForm] = useState({ name: "" });
  const [holidayForm, setHolidayForm] = useState({
    calendarId: "",
    date: "",
    name: "",
    isOptional: false,
  });

  const loadApprovals = async () => {
    try {
      const data = await leaveService.getLeaveRequests({ status: "Pending" });
      setPendingRequests(data || []);
    } catch (error) {
      toast({
        title: "Failed to load pending leaves",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  const loadPolicies = async () => {
    try {
      const data = await leaveService.getPolicies();
      setPolicies(data || []);
    } catch (error) {
      toast({
        title: "Failed to load policies",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  const loadCalendars = async () => {
    try {
      const data = await leaveService.listHolidayCalendars();
      setCalendars(data || []);
    } catch (error) {
      toast({
        title: "Failed to load calendars",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  useEffect(() => {
    setTab(defaultTab);
  }, [defaultTab]);

  useEffect(() => {
    loadApprovals();
    loadPolicies();
    loadCalendars();
  }, []);

  const approve = async (id) => {
    try {
      await leaveService.approveLeaveRequest(id);
      toast({ title: "Leave approved", status: "success" });
      loadApprovals();
    } catch (error) {
      toast({
        title: "Approve failed",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  const reject = async (id) => {
    try {
      await leaveService.rejectLeaveRequest(
        id,
        rejectReasonById[id] || "Rejected by approver",
      );
      toast({ title: "Leave rejected", status: "success" });
      setRejectReasonById((prev) => ({ ...prev, [id]: "" }));
      loadApprovals();
    } catch (error) {
      toast({
        title: "Reject failed",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  const createPolicy = async () => {
    if (!policyForm.name || !policyForm.code) {
      toast({ title: "Policy name and code are required", status: "warning" });
      return;
    }

    try {
      await leaveService.createPolicy({
        ...policyForm,
        accrual: {
          monthly: true,
          accrualRatePerMonth: Number(policyForm.yearlyQuota) / 12,
        },
      });
      toast({ title: "Policy created", status: "success" });
      setPolicyForm({
        name: "",
        code: "",
        type: "Paid",
        yearlyQuota: 12,
        carryForwardAllowed: true,
        maxCarryForward: 5,
        requiresApproval: true,
        allowHalfDay: true,
      });
      loadPolicies();
    } catch (error) {
      toast({
        title: "Policy create failed",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  const createCalendar = async () => {
    if (!calendarForm.name) {
      toast({ title: "Calendar name is required", status: "warning" });
      return;
    }

    try {
      await leaveService.createHolidayCalendar({ name: calendarForm.name });
      toast({ title: "Holiday calendar created", status: "success" });
      setCalendarForm({ name: "" });
      loadCalendars();
    } catch (error) {
      toast({
        title: "Calendar create failed",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  const addHoliday = async () => {
    if (!holidayForm.calendarId || !holidayForm.date || !holidayForm.name) {
      toast({
        title: "Calendar, date, and holiday name are required",
        status: "warning",
      });
      return;
    }

    try {
      await leaveService.addHolidayToCalendar(holidayForm.calendarId, {
        date: holidayForm.date,
        name: holidayForm.name,
        isOptional: holidayForm.isOptional,
      });
      toast({ title: "Holiday added", status: "success" });
      setHolidayForm((prev) => ({ ...prev, date: "", name: "" }));
      loadCalendars();
    } catch (error) {
      toast({
        title: "Add holiday failed",
        description: error.response?.data?.error || error.message,
        status: "error",
      });
    }
  };

  const tabButton = (value, label) => (
    <Button
      key={value}
      size="sm"
      variant={tab === value ? "solid" : "outline"}
      colorScheme="teal"
      onClick={() => setTab(value)}
    >
      {label}
    </Button>
  );

  const pendingCount = pendingRequests.length;
  const policyCount = policies.length;
  const calendarCount = calendars.length;
  const holidayCount = calendars.reduce(
    (total, calendar) => total + (calendar.holidays?.length || 0),
    0,
  );

  return (
    <Box p={{ base: 4, md: 6 }} bg={subtleBg} minH="100%">
      <VStack align="stretch" spacing={6}>
        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="xl"
          p={{ base: 4, md: 5 }}
        >
          <VStack align="stretch" spacing={4}>
            <Box>
              <Heading size="lg">Leave Admin Console</Heading>
              <Text mt={1} color="gray.500">
                Approvals, leave policies, and holiday calendar management.
              </Text>
            </Box>

            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
              <Box
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="lg"
                p={3}
              >
                <Text fontSize="xs" color="gray.500">
                  Pending Approvals
                </Text>
                <Heading size="md">{pendingCount}</Heading>
              </Box>
              <Box
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="lg"
                p={3}
              >
                <Text fontSize="xs" color="gray.500">
                  Leave Policies
                </Text>
                <Heading size="md">{policyCount}</Heading>
              </Box>
              <Box
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="lg"
                p={3}
              >
                <Text fontSize="xs" color="gray.500">
                  Holiday Calendars
                </Text>
                <Heading size="md">{calendarCount}</Heading>
              </Box>
              <Box
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="lg"
                p={3}
              >
                <Text fontSize="xs" color="gray.500">
                  Total Holidays
                </Text>
                <Heading size="md">{holidayCount}</Heading>
              </Box>
            </SimpleGrid>
          </VStack>
        </Box>

        <HStack spacing={2} flexWrap="wrap">
          {tabButton("approvals", "Approvals")}
          {tabButton("policies", "Policies")}
          {tabButton("holidays", "Holiday Calendars")}
        </HStack>

        {tab === "approvals" && (
          <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="xl"
            p={4}
          >
            <HStack justify="space-between" mb={3}>
              <Heading size="sm">Pending Leave Requests</Heading>
              <Badge colorScheme="orange">{pendingCount} Pending</Badge>
            </HStack>

            {!canApprove && (
              <Box
                bg="orange.50"
                borderWidth="1px"
                borderColor="orange.200"
                borderRadius="lg"
                p={3}
                mb={4}
              >
                <Text fontSize="sm" fontWeight="500" color="orange.800">
                  You don't have permission to approve or reject leave requests.
                </Text>
                <Text fontSize="xs" color="orange.700" mt={1}>
                  Only Admin, HRHead, HRExecutive, Manager, and SuperAdmin roles
                  can perform approvals.
                </Text>
              </Box>
            )}

            {pendingRequests.length === 0 ? (
              <Box
                py={10}
                textAlign="center"
                borderWidth="1px"
                borderRadius="lg"
              >
                <Text fontWeight="600">No pending leave approvals</Text>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  New requests will appear here.
                </Text>
              </Box>
            ) : (
              <>
                <Box display={{ base: "none", md: "block" }}>
                  <Table size="sm" variant="striped">
                    <Thead>
                      <Tr>
                        <Th>Employee</Th>
                        <Th>Policy</Th>
                        <Th>From</Th>
                        <Th>To</Th>
                        <Th>Status</Th>
                        <Th>Reject Reason</Th>
                        {canApprove && <Th>Action</Th>}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {pendingRequests.map((item) => (
                        <Tr key={item._id}>
                          <Td>{item.userId?.name || "-"}</Td>
                          <Td>{item.leavePolicyId?.name || "-"}</Td>
                          <Td>
                            {new Date(item.fromDate).toLocaleDateString()}
                          </Td>
                          <Td>{new Date(item.toDate).toLocaleDateString()}</Td>
                          <Td>
                            <Badge colorScheme="orange">{item.status}</Badge>
                          </Td>
                          <Td>
                            <Input
                              size="sm"
                              placeholder="Reason"
                              isDisabled={!canApprove}
                              value={rejectReasonById[item._id] || ""}
                              onChange={(e) =>
                                setRejectReasonById((prev) => ({
                                  ...prev,
                                  [item._id]: e.target.value,
                                }))
                              }
                            />
                          </Td>
                          {canApprove && (
                            <Td>
                              <HStack>
                                <Button
                                  size="xs"
                                  colorScheme="green"
                                  onClick={() => approve(item._id)}
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="xs"
                                  colorScheme="red"
                                  onClick={() => reject(item._id)}
                                >
                                  Reject
                                </Button>
                              </HStack>
                            </Td>
                          )}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>

                <Box display={{ base: "block", md: "none" }}>
                  <SimpleGrid columns={1} spacing={3}>
                    {pendingRequests.map((item) => (
                      <Box
                        key={item._id}
                        borderWidth="1px"
                        borderRadius="md"
                        p={3}
                      >
                        <HStack justify="space-between" mb={2}>
                          <Text fontWeight="600">
                            {item.userId?.name || "-"}
                          </Text>
                          <Badge colorScheme="orange">{item.status}</Badge>
                        </HStack>
                        <Text fontSize="sm" color="gray.600">
                          Policy: {item.leavePolicyId?.name || "-"}
                        </Text>
                        <Text fontSize="sm" color="gray.600" mb={2}>
                          {new Date(item.fromDate).toLocaleDateString()} -{" "}
                          {new Date(item.toDate).toLocaleDateString()}
                        </Text>
                        {canApprove && (
                          <>
                            <Input
                              size="sm"
                              placeholder="Reason"
                              value={rejectReasonById[item._id] || ""}
                              onChange={(e) =>
                                setRejectReasonById((prev) => ({
                                  ...prev,
                                  [item._id]: e.target.value,
                                }))
                              }
                            />
                            <HStack mt={2}>
                              <Button
                                size="sm"
                                colorScheme="green"
                                onClick={() => approve(item._id)}
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                colorScheme="red"
                                onClick={() => reject(item._id)}
                              >
                                Reject
                              </Button>
                            </HStack>
                          </>
                        )}
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
              </>
            )}
          </Box>
        )}

        {tab === "policies" && (
          <VStack align="stretch" spacing={4}>
            {!canApprove && (
              <Box
                bg="orange.50"
                borderWidth="1px"
                borderColor="orange.200"
                borderRadius="lg"
                p={3}
              >
                <Text fontSize="sm" fontWeight="500" color="orange.800">
                  You don't have permission to manage leave policies.
                </Text>
                <Text fontSize="xs" color="orange.700" mt={1}>
                  Only Admin, HRHead, HRExecutive, Manager, and SuperAdmin roles
                  can manage policies.
                </Text>
              </Box>
            )}
            <Box
              bg={cardBg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="xl"
              p={4}
              opacity={!canApprove ? 0.6 : 1}
            >
              <Heading size="sm" mb={3}>
                Create Policy
              </Heading>
              <Grid
                templateColumns={{
                  base: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(4, minmax(0, 1fr))",
                }}
                gap={3}
              >
                <FormControl>
                  <FormLabel>Name</FormLabel>
                  <Input
                    isDisabled={!canApprove}
                    value={policyForm.name}
                    onChange={(e) =>
                      setPolicyForm((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Code</FormLabel>
                  <Input
                    isDisabled={!canApprove}
                    value={policyForm.code}
                    onChange={(e) =>
                      setPolicyForm((prev) => ({
                        ...prev,
                        code: e.target.value,
                      }))
                    }
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Type</FormLabel>
                  <Select
                    isDisabled={!canApprove}
                    value={policyForm.type}
                    onChange={(e) =>
                      setPolicyForm((prev) => ({
                        ...prev,
                        type: e.target.value,
                      }))
                    }
                  >
                    <option value="Paid">Paid</option>
                    <option value="Unpaid">Unpaid</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Yearly Quota</FormLabel>
                  <NumberInput
                    isDisabled={!canApprove}
                    value={policyForm.yearlyQuota}
                    onChange={(_, value) =>
                      setPolicyForm((prev) => ({
                        ...prev,
                        yearlyQuota: value || 0,
                      }))
                    }
                  >
                    <NumberInputField />
                  </NumberInput>
                </FormControl>
              </Grid>
              <Button
                mt={4}
                colorScheme="teal"
                isDisabled={!canApprove}
                onClick={createPolicy}
              >
                Create Policy
              </Button>
            </Box>

            <Box
              bg={cardBg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="xl"
              p={4}
            >
              <HStack justify="space-between" mb={3}>
                <Heading size="sm">Existing Policies</Heading>
                <Badge colorScheme="teal">{policyCount} Policies</Badge>
              </HStack>

              {policies.length === 0 ? (
                <Box
                  py={10}
                  textAlign="center"
                  borderWidth="1px"
                  borderRadius="lg"
                >
                  <Text fontWeight="600">No leave policies found</Text>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Create your first policy from the form above.
                  </Text>
                </Box>
              ) : (
                <>
                  <Box display={{ base: "none", md: "block" }}>
                    <Table size="sm" variant="striped">
                      <Thead>
                        <Tr>
                          <Th>Name</Th>
                          <Th>Code</Th>
                          <Th>Type</Th>
                          <Th isNumeric>Quota</Th>
                          <Th>Status</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {policies.map((policy) => (
                          <Tr key={policy._id}>
                            <Td>{policy.name}</Td>
                            <Td>{policy.code}</Td>
                            <Td>{policy.type}</Td>
                            <Td isNumeric>{policy.yearlyQuota}</Td>
                            <Td>
                              <Badge
                                colorScheme={policy.isActive ? "green" : "gray"}
                              >
                                {policy.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>

                  <Box display={{ base: "block", md: "none" }}>
                    <SimpleGrid columns={1} spacing={3}>
                      {policies.map((policy) => (
                        <Box
                          key={policy._id}
                          borderWidth="1px"
                          borderRadius="md"
                          p={3}
                        >
                          <HStack justify="space-between" mb={2}>
                            <Text fontWeight="600">{policy.name}</Text>
                            <Badge
                              colorScheme={policy.isActive ? "green" : "gray"}
                            >
                              {policy.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </HStack>
                          <SimpleGrid columns={2} spacing={2}>
                            <Box>
                              <Text fontSize="xs" color="gray.500">
                                Code
                              </Text>
                              <Text fontSize="sm">{policy.code}</Text>
                            </Box>
                            <Box>
                              <Text fontSize="xs" color="gray.500">
                                Type
                              </Text>
                              <Text fontSize="sm">{policy.type}</Text>
                            </Box>
                            <Box>
                              <Text fontSize="xs" color="gray.500">
                                Quota
                              </Text>
                              <Text fontSize="sm">{policy.yearlyQuota}</Text>
                            </Box>
                          </SimpleGrid>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Box>
                </>
              )}
            </Box>
          </VStack>
        )}

        {tab === "holidays" && (
          <VStack align="stretch" spacing={4}>
            {!canApprove && (
              <Box
                bg="orange.50"
                borderWidth="1px"
                borderColor="orange.200"
                borderRadius="lg"
                p={3}
              >
                <Text fontSize="sm" fontWeight="500" color="orange.800">
                  You don't have permission to manage holiday calendars.
                </Text>
                <Text fontSize="xs" color="orange.700" mt={1}>
                  Only Admin, HRHead, HRExecutive, Manager, and SuperAdmin roles
                  can manage calendars.
                </Text>
              </Box>
            )}
            <Box
              bg={cardBg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="xl"
              p={4}
              opacity={!canApprove ? 0.6 : 1}
            >
              <Heading size="sm" mb={3}>
                Create Holiday Calendar
              </Heading>
              <Stack direction={{ base: "column", md: "row" }} spacing={3}>
                <Input
                  isDisabled={!canApprove}
                  placeholder="Calendar name"
                  value={calendarForm.name}
                  onChange={(e) =>
                    setCalendarForm((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
                <Button
                  isDisabled={!canApprove}
                  colorScheme="teal"
                  onClick={createCalendar}
                >
                  Create
                </Button>
              </Stack>
            </Box>

            <Box
              bg={cardBg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="xl"
              p={4}
              opacity={!canApprove ? 0.6 : 1}
            >
              <Heading size="sm" mb={3}>
                Add Holiday
              </Heading>
              <Grid
                templateColumns={{
                  base: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(4, minmax(0, 1fr))",
                }}
                gap={3}
              >
                <Select
                  isDisabled={!canApprove}
                  placeholder="Select calendar"
                  value={holidayForm.calendarId}
                  onChange={(e) =>
                    setHolidayForm((prev) => ({
                      ...prev,
                      calendarId: e.target.value,
                    }))
                  }
                >
                  {calendars.map((calendar) => (
                    <option key={calendar._id} value={calendar._id}>
                      {calendar.name}
                    </option>
                  ))}
                </Select>
                <Input
                  isDisabled={!canApprove}
                  type="date"
                  value={holidayForm.date}
                  onChange={(e) =>
                    setHolidayForm((prev) => ({
                      ...prev,
                      date: e.target.value,
                    }))
                  }
                />
                <Input
                  isDisabled={!canApprove}
                  placeholder="Holiday name"
                  value={holidayForm.name}
                  onChange={(e) =>
                    setHolidayForm((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
                <Button
                  isDisabled={!canApprove}
                  colorScheme="teal"
                  onClick={addHoliday}
                >
                  Add Holiday
                </Button>
              </Grid>
            </Box>

            <Box
              bg={cardBg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="xl"
              p={4}
            >
              <HStack justify="space-between" mb={3}>
                <Heading size="sm">Holiday Calendars</Heading>
                <Badge colorScheme="purple">{calendarCount} Calendars</Badge>
              </HStack>

              {calendars.length === 0 ? (
                <Box
                  py={10}
                  textAlign="center"
                  borderWidth="1px"
                  borderRadius="lg"
                >
                  <Text fontWeight="600">No holiday calendars found</Text>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Create a calendar to start adding company holidays.
                  </Text>
                </Box>
              ) : (
                <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
                  {calendars.map((calendar) => {
                    const sortedHolidays = [...(calendar.holidays || [])].sort(
                      (a, b) => new Date(a.date) - new Date(b.date),
                    );

                    return (
                      <Box
                        key={calendar._id}
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="lg"
                        p={4}
                      >
                        <HStack justify="space-between" mb={2}>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="700">{calendar.name}</Text>
                            <Text fontSize="xs" color="gray.500">
                              {sortedHolidays.length} holidays configured
                            </Text>
                          </VStack>
                          <Badge
                            colorScheme={calendar.isActive ? "green" : "gray"}
                          >
                            {calendar.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </HStack>

                        <Divider mb={3} />

                        {sortedHolidays.length === 0 ? (
                          <Text fontSize="sm" color="gray.500">
                            No holidays added yet.
                          </Text>
                        ) : (
                          <VStack align="stretch" spacing={2}>
                            {sortedHolidays.map((holiday) => (
                              <HStack
                                key={holiday._id}
                                justify="space-between"
                                borderWidth="1px"
                                borderColor={borderColor}
                                borderRadius="md"
                                p={2}
                              >
                                <Text fontSize="sm" fontWeight="500">
                                  {holiday.name}
                                </Text>
                                <HStack spacing={2}>
                                  <Text fontSize="xs" color="gray.500">
                                    {new Date(
                                      holiday.date,
                                    ).toLocaleDateString()}
                                  </Text>
                                  {holiday.isOptional && (
                                    <Badge size="sm" colorScheme="yellow">
                                      Optional
                                    </Badge>
                                  )}
                                </HStack>
                              </HStack>
                            ))}
                          </VStack>
                        )}
                      </Box>
                    );
                  })}
                </SimpleGrid>
              )}
            </Box>
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

export default LeaveApproval;
